import { Injectable } from "@angular/core";
import { io, Socket } from "socket.io-client";
import { BehaviorSubject, Observable } from "rxjs";
import { environment } from "../environments/environment";

interface AuthData {
	identifier: string;
	name: string;
}

@Injectable({
	providedIn: "root",
})
export class WebsocketService {
	private socket!: Socket;
	private reconnectData?: AuthData; // 🔹 guarda la última data usada para reconexión

	/* Controla si se permite reconectar (logout lo apaga) */
	private allowReconnect = true;

	/* Guarda el timer de reconexión para poder cancelarlo */
	private reconnectTimerId: any = null;

  private connectionStatus$ = new BehaviorSubject<'disconnected' | 'connecting' | 'connected' | 'reconnecting'>('disconnected');

	constructor() {
		// 🔹 Al instanciar el servicio, intentamos cargar credenciales previas
		// this.restoreReconnectData();
	}

	private restoreReconnectData(): void {
		try {
			const raw = localStorage.getItem("reconnectData");
			if (!raw) return;

			const parsed = JSON.parse(raw);
			if (parsed?.identifier && parsed?.name) {
				this.reconnectData = parsed as AuthData;
			} else {
				console.warn(
					"⚠️ reconnectData inválido en storage, limpiando.",
				);
				localStorage.removeItem("reconnectData");
			}
		} catch (e) {
			console.error("⚠️ Error parseando reconnectData del storage:", e);
			localStorage.removeItem("reconnectData");
		}
	}

  getConnectionStatus(): Observable<'disconnected' | 'connecting' | 'connected' | 'reconnecting'> {
    return this.connectionStatus$.asObservable();
  }

  /* Permite escuchar cuando el socket se conecta */
  onConnect(): Observable<string> {
    return new Observable<string>((subscriber) => {
      this.socket.on("connect", () => {
        this.connectionStatus$.next('connected');
        subscriber.next(this.socket.id);
      });
    });
  }

  connectAndWait(data: AuthData): Promise<string> {
  return new Promise((resolve, reject) => {

    if (this.socket && this.socket.connected) {
      resolve(this.socket.id);
		}

    this.connect(data);

    this.socket.once("connect", () => {
      resolve(this.socket.id);
    });

    this.socket.once("connect_error", (err) => {
      reject(err);
    });

  });
}

	connect(data?: AuthData): void {
		// if (!this.allowReconnect) {
    //   console.log("No se permite reconexión")
		// 	return;
		// }
		// si ya existe conexión, no reconectamos
    const token = localStorage.getItem('access_token');
    if (!token) {
      return;
    }
		if (this.socket && this.socket.connected) {
      this.connectionStatus$.next('connected');
      console.log("ya reconectado")
			return;
		}

		// 🔹 guarda credenciales para posible reconexión automática
		if (data) {
			this.reconnectData = data;
			localStorage.setItem(
				"reconnectData",
				JSON.stringify(this.reconnectData),
			);
		}

		// si no hay data ni guardada, no puede conectarse
		let authPayload = this.reconnectData;
		if (!authPayload) {
			const authPayloadStorage = localStorage.getItem("reconnectData");
			let authPayloadStorageJson = JSON.parse(authPayloadStorage);
			if (!authPayloadStorageJson) {
        const user = JSON.parse(localStorage.getItem('user'))
        if(!user){
          console.error(
            "❌ No hay datos de autenticación para conectar el WebSocket",
          );
          return;
        }
        authPayloadStorageJson = {
          identifier: user.id.toString(),
          name: user.name
        }
			}
			authPayload = authPayloadStorageJson;
		}

		// crea el socket
		this.socket = io(environment.apis.socketUrl, {
			transports: ["websocket"],
			auth: {
				identifier: authPayload.identifier,
				name: authPayload.name,
        pass: "Hj@P&@3my1C9!P3H8pK2"
			},
		});

		this.registerDefaultHandlers();
	}

	private registerDefaultHandlers(): void {
		this.socket.on("connect", () => {
      this.connectionStatus$.next('connected');
		});

		this.socket.on("unauthorized", (data) => {
			console.error("🚫 Desconectado: ", data.reason);
		});

		this.socket.on("disconnect", (reason) => {
			console.log("❌ Desconectado del servidor. Motivo:", reason);
      this.connectionStatus$.next('disconnected');

			// 🔁 intento de reconexión automático
			// this.tryReconnect();
		});
	}

	private tryReconnect(): void {
		if (!this.allowReconnect) {
			return;
		}

		const raw = localStorage.getItem("reconnectData");
		if (!raw) {
			return;
		}

		/* Evita programar múltiples reconexiones */
		if (this.reconnectTimerId) {
			clearTimeout(this.reconnectTimerId);
			this.reconnectTimerId = null;
		}

		console.log("🔄 Intentando reconexión automática...");
		this.reconnectTimerId = setTimeout(() => {
			this.reconnectTimerId = null;
			this.connect();
		}, 3000);
	}

	disconnect(): void {
		/* Bloquea reconexiones futuras */
		this.allowReconnect = false;
    this.connectionStatus$.next('disconnected');

		/* Cancela reconexión pendiente */
		if (this.reconnectTimerId) {
			clearTimeout(this.reconnectTimerId);
			this.reconnectTimerId = null;
		}

		/* Limpia data */
		this.reconnectData = undefined;
		localStorage.removeItem("reconnectData");

		if (!this.socket) {
			return;
		}

		/* Quita listeners para que no dispare tryReconnect() */
		this.socket.removeAllListeners();

		/* Deshabilita reconexión nativa de socket.io (por si estuviera activa) */
		this.socket.io.opts.reconnection = false;

		/* Desconecta */
		this.socket.disconnect();

		/* Libera referencia */
		this.socket = undefined as any;
	}

	/** 🔹 Quita un listener específico para evitar duplicados */
	off(event: string): void {
		if (this.socket) {
			this.socket.off(event);
			console.log(`🧹 Listener eliminado`);
		}
	}

	// 🔹 Emitir eventos
	emit(event: string, ...args: any[]): void {
		if (this.socket && this.socket.connected) {
			this.socket.emit(event, ...args);
			return;
		}

		console.warn(`⚠️ No se pudo emitir '${event}', socket no conectado.`);

		if (this.allowReconnect) {
			this.connect();
		}
	}

	// 🔹 Escuchar eventos
	listen<T>(event: string): Observable<T> {
		return new Observable<T>((subscriber) => {
			this.socket.on(event, (data: T) => {
				subscriber.next(data);
			});
		});
	}
}
